import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { canUseAnalytics } from '@/services/consent';
import type { ConsentSettings } from '@/services/consent';
import { API_ENDPOINTS } from '../api/config';
// MR2 (#100): GA4 adapter. Receives the same already-sanitized payload Mixpanel
// gets, so it can never forward extra PII. Disabled when its measurement id env
// var is empty (independent rollback from Mixpanel/funnel).
import { ga4Provider, initGa4, resetGa4ForTests } from './ga4';

// Token MUST come from the environment only. The hardcoded production fallback
// was removed (#91): a build without a token simply does not initialize
// Mixpanel, so no dev events ever reach the prod project. Which builds carry a
// token is decided at build time — see docs/analytics/build-time-env.md (#298).
// Staging does carry one; it is told apart by the `environment` super-property.
//
// `disabled` is an explicit off switch, not a token. A profile-level `env` in
// `eas.json` is what overrides the value stored in EAS, and that is how the
// development and e2e-test profiles stay out of the production project — but
// `eas.json` rejects an empty string outright ("is not allowed to be empty"),
// which broke every `eas` command until this sentinel replaced it (#301).
export const ANALYTICS_DISABLED = 'disabled';
const rawMixpanelToken = process.env.EXPO_PUBLIC_MIXPANEL_TOKEN || '';
const MIXPANEL_TOKEN = rawMixpanelToken === ANALYTICS_DISABLED ? '' : rawMixpanelToken;
const MIXPANEL_SERVER_URL = 'https://api-eu.mixpanel.com';

const APP_VERSION = Constants.expoConfig?.version ?? 'unknown';

// ---------------------------------------------------------------------------
// Environment super-property (#100 / #91)
// ---------------------------------------------------------------------------
// `preview` is the variant name this repo actually uses for staging — both the
// staging web image (.gitlab-ci.yml BUILD_APP_VARIANT) and the EAS `preview`
// profile. It was missing here, so every staging build labelled itself
// `production` (#298). That only became visible once staging builds started
// carrying a Mixpanel token: without the mapping, the `environment=production`
// dashboard filter from #91 would show staging traffic as production.
// A build with no/unknown app-variant MUST NOT silently masquerade as
// production (#298 regression guard): production is only opted into by an
// explicit `production` variant. Anything else (`''`, misconfigured, unknown)
// is reported as `unknown` so staging dashboards and prod filters never absorb
// mislabelled traffic.
export type AnalyticsEnvironment = 'development' | 'staging' | 'production' | 'unknown';

export function resolveEnvironment(): AnalyticsEnvironment {
  if (__DEV__) return 'development';
  const variant = (process.env.EXPO_PUBLIC_APP_VARIANT || '').toLowerCase();
  if (variant === 'development') return 'development';
  if (variant === 'preview' || variant === 'staging' || variant === 'beta') return 'staging';
  if (variant === 'production') return 'production';
  return 'unknown';
}

const ENVIRONMENT = resolveEnvironment();

// ---------------------------------------------------------------------------
// Canonical event contract + PII allowlist (#100)
// Mirrors backend/metrics/events.py::FUNNEL_CONTRACT. Kept in sync manually;
// the backend is the authoritative source, this is the client-side guard.
// ---------------------------------------------------------------------------
type EventName = string;

const EVENT_ALLOWED_PROPS: Record<EventName, string[]> = {
  funnel_entry_viewed: ['entry_point', 'source', 'campaign', 'user_role', 'app_variant'],
  signup_started: ['user_role', 'entry_point', 'source', 'app_variant'],
  signup_completed: ['user_role', 'auth_method', 'app_variant'],
  role_selected: ['user_role', 'role', 'app_variant'],
  onboarding_started: ['user_role', 'flow', 'app_variant'],
  onboarding_completed: [
    'user_role',
    'flow',
    'fleet_size_range',
    'onboarding_outcome',
    'payer',
    'app_variant',
  ],
  plan_selected: ['user_role', 'plan', 'feature', 'billing', 'app_variant'],
  pricing_viewed: ['user_role', 'feature', 'billing', 'app_variant'],
  checkout_started: [
    'user_role',
    'feature',
    'plan',
    'billing',
    'price_id',
    'payer',
    'app_variant',
  ],
  payment_submitted: [
    'user_role',
    'feature',
    'plan',
    'billing',
    'price_id',
    'app_variant',
  ],
  payment_failed: ['user_role', 'reason', 'feature', 'plan', 'app_variant'],
  purchase_completed: [
    'user_role',
    'transaction_id',
    'currency',
    'licenses',
    'feature',
    'plan',
    'billing',
    'is_new',
    'source',
    'payer',
    'app_variant',
  ],
  activation_completed: ['user_role', 'flow', 'onboarding_outcome', 'payer', 'app_variant'],
};

// Product (non-funnel) events. Same consent, PII and allowlist rules as the
// funnel, but they are not part of FUNNEL_CONTRACT, so they never reach the
// anonymous funnel counters and stay out of the backend parity gate.
const GPS_CONTRACT_DECLINE_PROPS = ['screen', 'feature', 'billing', 'licenses', 'user_role', 'app_variant'];

const PRODUCT_EVENT_PROPS: Record<EventName, string[]> = {
  gps_contract_declined: GPS_CONTRACT_DECLINE_PROPS,
  gps_contract_declined_to_hours: GPS_CONTRACT_DECLINE_PROPS,
  // ALL-136: driving limits on the driver home. Limit key and level only.
  driver_limit_warning_shown: ['limit', 'level', 'source', 'user_role', 'app_variant'],
  driver_limits_opened: ['source', 'user_role', 'app_variant'],
};

const allowedPropsFor = (eventName: EventName): string[] | undefined =>
  Object.prototype.hasOwnProperty.call(EVENT_ALLOWED_PROPS, eventName)
    ? EVENT_ALLOWED_PROPS[eventName]
    : Object.prototype.hasOwnProperty.call(PRODUCT_EVENT_PROPS, eventName)
      ? PRODUCT_EVENT_PROPS[eventName]
      : undefined;

// Keys that must NEVER leave the device in an analytics payload.
const PII_KEY_BLACKLIST = new Set([
  'name',
  'first_name',
  'last_name',
  'full_name',
  'email',
  'phone',
  'phone_number',
  'mobile',
  'address',
  'street',
  'city',
  'postal',
  'zip',
  'zipcode',
  'card',
  'card_number',
  'cardnumber',
  'pan',
  'cvv',
  'cvc',
  'token',
  'stripe_token',
  'secret',
  'api_key',
  'apikey',
  'password',
  'raw_error',
  'error_message',
  'error_stack',
  'company_id',
  'company_name',
  'ip',
  'ip_address',
  'device_id',
  'advertising_id',
  'idfa',
  'aaid',
  'user_id',
]);

const PII_KEY_SUBSTRINGS = [
  'email',
  'phone',
  'address',
  'card',
  'token',
  'secret',
  'password',
  'ssn',
  'tax_id',
  'vat_id',
  'passport',
  'license_plate',
  'company_id',
  'user_id',
  'error',
  'detail',
];

function keyIsPii(key: string): boolean {
  const k = key.toLowerCase();
  if (PII_KEY_BLACKLIST.has(k)) return true;
  return PII_KEY_SUBSTRINGS.some((sub) => k.includes(sub));
}

function safeInfraValue(key: string, value: unknown): boolean {
  if (key === 'environment') {
    return value === 'development' || value === 'staging' || value === 'production';
  }
  if (key === 'event_id') {
    return (
      typeof value === 'string' &&
      value.length > 0 &&
      value.length <= 128 &&
      /^[A-Za-z0-9][A-Za-z0-9:_-]*$/.test(value)
    );
  }
  return false;
}

function safeAnalyticsValue(value: unknown): value is string | number | boolean {
  return (
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    (typeof value === 'number' && Number.isFinite(value))
  );
}

/**
 * Strip PII and enforce the per-event allowlist before any analytics payload
 * leaves the device. `event_id` and `environment` are infra keys and are
 * always preserved only after validation. Unknown events fail closed.
 */
export function sanitizeProperties(
  eventName: EventName,
  properties: Record<string, any> = {},
): Record<string, any> {
  if (!properties || typeof properties !== 'object') return {};
  const allow = allowedPropsFor(eventName);
  const out: Record<string, any> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (key === 'event_id' || key === 'environment') {
      if (safeInfraValue(key, value)) out[key] = value;
      continue;
    }
    if (!allow || keyIsPii(key) || !allow.includes(key)) continue;
    if (!safeAnalyticsValue(value)) continue;
    out[key] = value;
  }
  return out;
}

// ---------------------------------------------------------------------------
// TEST-ONLY E2E observer hook (#389 blocker 6 fix)
// ---------------------------------------------------------------------------
// The previous E2E approach monkeypatched the external `window.mixpanel` object.
// The Mixpanel loader can overwrite that stub at any time, so the capture was
// unreliable, and it weakened the consent story by fabricating provider methods.
//
// This hook sits at the REAL analytics adapter dispatch boundary — the exact
// point where consent-approved code is about to call a provider `track`/
// `identify` (and separately where the consent-off anonymous funnel is sent).
// It is enabled ONLY when the E2E harness sets an explicit flag, it carries only
// already-sanitized/allowlisted event metadata, it never changes consent, and
// because it lives inside our adapter it CANNOT be overwritten by the Mixpanel
// loader. E2E reads the provider-track / provider-identify / anonymous-funnel
// channels from this single hook.
export type E2EObserverChannel = 'provider' | 'anonymous';

export interface E2EObserverEntry {
  name: string;
  props: Record<string, any>;
  channel: E2EObserverChannel;
}

type E2EObserverSink = (entry: E2EObserverEntry) => void;

const E2E_OBSERVER_FLAG = '__ALLDRIVERS_E2E_ANALYTICS_OBSERVER__';
const E2E_OBSERVER_SINK = '__ALLDRIVERS_E2E_OBSERVER_SINK__';

const e2eObserverEnabled = (): boolean =>
  typeof window !== 'undefined' && (window as any)[E2E_OBSERVER_FLAG] === true;

const e2eObserverSink = (): E2EObserverSink | null => {
  if (typeof window === 'undefined') return null;
  const sink = (window as any)[E2E_OBSERVER_SINK];
  return typeof sink === 'function' ? (sink as E2EObserverSink) : null;
};

/**
 * Install the E2E observer. Honoured ONLY when the harness flag is already set,
 * so this is a guaranteed no-op in every production build (the flag is never set
 * outside the E2E harness). This is what makes the capture impossible to enable
 * by accident and impossible for the Mixpanel loader to subvert.
 */
export const installAnalyticsE2EObserver = (sink: E2EObserverSink): void => {
  if (!e2eObserverEnabled()) return;
  (window as any)[E2E_OBSERVER_SINK] = sink;
};

const emitE2EObserver = (
  name: string,
  props: Record<string, any>,
  channel: E2EObserverChannel,
): void => {
  if (!e2eObserverEnabled()) return;
  const sink = e2eObserverSink();
  if (!sink) return;
  // Forward only already-sanitized, allowlisted metadata. Provider payloads are
  // the post-sanitize `clean` object; the anonymous funnel body is built from
  // safe coarse dimensions only. No raw/PII context is ever attached here.
  sink({ name, props, channel });
};

// ---------------------------------------------------------------------------
// event_id dedupe (#100)
// One action => at most one event. A caller may pass event_id for idempotent
// retries; otherwise we mint one. We keep a short-lived in-memory set so the
// same event_id is never dispatched twice from this session (mirrors the
// backend's event_id idempotency guard).
// ---------------------------------------------------------------------------
const seenEventIds = new Set<string>();
const EVENT_ID_TTL_MS = 1000 * 60 * 60 * 24; // 24h

function mintEventId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Deterministic semantic event id for a stable journey context.
 *
 * Component-local random UUIDs (Date.now()+Math.random, or crypto.randomUUID)
 * are INVALID for dedupe: a React StrictMode double-mount — or any unmount/
 * remount of the same logical journey — produces a fresh random id each time,
 * so the dedupe set can never collapse the duplicate emission. We instead
 * derive the id from stable journey context that survives remount AND still
 * lets a genuinely new journey produce a distinct id.
 *
 * The id is built from stable segments, ALL of which are included when present
 * (none silently shadows another):
 *   - `user-<id>`    when an authenticated user id is available
 *   - `<flow>`       the journey/flow discriminator (e.g. `owner_pricing`)
 *   - `<stableKey>`  a STABLE JOURNEY INSTANCE key supplied by the caller
 *     (survives remount/StrictMode but is ROTATED at a real journey boundary
 *     such as onboarding completion / new onboarding start)
 *
 * Composition order: `${eventName}:user-<id>?<:flow>?<:stableKey>?`.
 *
 * Why stableKey is appended even when a userId exists: a userId+flow pair ALONE
 * would suppress every future same-user journey (the same owner re-entering
 * onboarding would collapse to the original emission). The journey instance key
 * is what makes "same journey remount = one event" yet "same user after a
 * journey reset = a distinct, second event" possible. Falls back to a random id
 * only when no stable context exists at all.
 */
export function makeSemanticEventId(
  eventName: string,
  ctx: { userId?: string | null; flow?: string; stableKey?: string } = {},
): string {
  const { userId, flow, stableKey } = ctx;
  const parts: string[] = [eventName];
  if (userId != null && String(userId).length > 0) {
    parts.push(`user-${String(userId)}`);
  }
  if (flow) {
    parts.push(flow);
  }
  if (stableKey) {
    parts.push(stableKey);
  }
  if (parts.length === 1) {
    // No stable context: random, but flagged so callers can detect the gap.
    return `evt_${eventName}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
  return parts.join(':');
}

function dedupeEventId(eventId: string): boolean {
  if (seenEventIds.has(eventId)) return false;
  seenEventIds.add(eventId);
  // best-effort TTL cleanup
  setTimeout(() => seenEventIds.delete(eventId), EVENT_ID_TTL_MS).unref?.();
  return true;
}

// ---------------------------------------------------------------------------
// Provider abstraction (Mixpanel now, GA4 in MR2)
// ---------------------------------------------------------------------------
export interface AnalyticsProvider {
  readonly name: string;
  track(eventName: string, properties: Record<string, any>): void;
  identify(distinctId: string | number): void;
  setUserProperties(properties: Record<string, any>): void;
  registerSuperProperties(properties: Record<string, any>): void;
  flush(): void;
}

// Anonymous funnel fallback — used when consent is absent. Sends ONLY the
// canonical event name + coarse dimensions to our own backend. No Mixpanel/GA.
const ANONYMOUS_FUNNEL_EVENTS: Record<string, string> = {
  funnel_entry_viewed: 'funnel_entry_viewed',
  signup_started: 'signup_started',
  signup_completed: 'signup_completed',
  role_selected: 'role_selected',
  onboarding_started: 'onboarding_started',
  onboarding_completed: 'onboarding_completed',
  plan_selected: 'plan_selected',
  pricing_viewed: 'pricing_viewed',
  checkout_started: 'checkout_started',
  payment_submitted: 'payment_submitted',
  payment_failed: 'payment_failed',
  purchase_completed: 'purchase_completed',
  activation_completed: 'activation_completed',
  // legacy aliases
  registration_started: 'signup_started',
  'Sign Up': 'signup_completed',
  sign_up: 'signup_completed',
  fleet_size_declared: 'onboarding_completed',
  licenses_chosen: 'plan_selected',
  product_chosen: 'plan_selected',
  billing_cycle_chosen: 'plan_selected',
};

const sendAnonymousFunnelCount = (
  eventName: string,
  properties: Record<string, any>,
  eventId: string,
) => {
  const canonical = ANONYMOUS_FUNNEL_EVENTS[eventName];
  if (!canonical) return;
  const role =
    properties?.user_role === 'owner' || properties?.user_role === 'driver'
      ? properties.user_role
      : 'unknown';
  const clean = sanitizeProperties(eventName, properties);
  const onboardingOutcome = clean.onboarding_outcome;
  fetch(API_ENDPOINTS.FUNNEL_METRIC, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      event: canonical,
      event_id: eventId,
      environment: ENVIRONMENT,
      platform: Platform.OS,
      app_version: APP_VERSION,
      role,
      ...(typeof onboardingOutcome === 'string'
        ? { properties: { onboarding_outcome: onboardingOutcome } }
        : {}),
    }),
  }).catch(() => {
    // Fire-and-forget: losing a count must never affect the user flow.
  });
  // #389 blocker 6: capture the consent-off anonymous funnel at the REAL adapter
  // boundary (not via a fetch proxy). Channel 'anonymous' so the consent-off
  // control can allow it while still asserting ZERO provider calls.
  emitE2EObserver(canonical, {
    event: canonical,
    event_id: eventId,
    environment: ENVIRONMENT,
    role,
  }, 'anonymous');
};

let mixpanelClient: any = null;
let initPromise: Promise<void> | null = null;
let analyticsAllowed = false;
let lastIdentifiedId: string | null = null;

// Base super-properties attached to every event so Mixpanel funnels can be
// segmented by platform, app version and environment without touching each
// call site (#100).
const baseSuperProperties = (): Record<string, any> => ({
  platform: Platform.OS,
  app_version: APP_VERSION,
  environment: ENVIRONMENT,
});

const getWebClient = () => {
  if (typeof window === 'undefined') return null;
  return (window as any).mixpanel || null;
};

// ---------------------------------------------------------------------------
// Independent provider emission (#299)
// Each provider is emitted through its own guarded call. Neither sits inside
// the other's try/catch and neither is gated on the other's client existing,
// so a Mixpanel throw — or an absent Mixpanel token — can never silence GA4.
// That is what "independent rollback" in ga4.ts means.
// ---------------------------------------------------------------------------
const emitGa4 = (action: string, emit: () => void): void => {
  try {
    emit();
  } catch (error) {
    console.error(`GA4 ${action} failed:`, error);
  }
};

const emitMixpanel = async (action: string, emit: () => unknown): Promise<void> => {
  try {
    await emit();
  } catch (error) {
    console.error(`Mixpanel ${action} failed:`, error);
  }
};

const optOutWeb = () => {
  const webClient = getWebClient();
  webClient?.opt_out_tracking?.();
  webClient?.stop_session_recording?.();
};

const optOutNative = () => {
  try {
    mixpanelClient?.optOutTracking?.();
    mixpanelClient?.flush?.();
  } catch (error) {
    console.error('Mixpanel opt-out failed:', error);
  }
};

const optInNative = () => {
  try {
    mixpanelClient?.optInTracking?.();
  } catch (error) {
    console.error('Mixpanel opt-in failed:', error);
  }
};

// Register super-properties directly on the client, bypassing the
// ensureAnalyticsAllowed guard. Callers inside init/consent flows must use
// this (the public wrapper awaits initPromise and would otherwise deadlock).
const rawRegisterSuperProperties = async (properties: Record<string, any>) => {
  if (Platform.OS === 'web') {
    getWebClient()?.register?.(properties);
    return;
  }
  if (!mixpanelClient) return;
  try {
    await mixpanelClient.registerSuperProperties?.(properties);
  } catch (error) {
    console.error('Mixpanel registerSuperProperties failed:', error);
  }
};

const doInit = async () => {
  analyticsAllowed = await canUseAnalytics();

  if (Platform.OS === 'web') {
    if (!analyticsAllowed) {
      optOutWeb();
      try {
        ga4Provider.setConsent(false);
      } catch (error) {
        console.error('GA4 consent update failed:', error);
      }
    } else {
      await rawRegisterSuperProperties(baseSuperProperties());
      // MR2 (#100): load + enable GA4 only after consent. No measurement id =>
      // initGa4 is a no-op, so GA4 stays fully disabled (independent rollback).
      try {
        await initGa4();
        ga4Provider.setConsent(true);
      } catch (error) {
        console.error('GA4 init failed:', error);
      }
    }
    return;
  }

  // No token => do not initialize Mixpanel at all (#91). This keeps dev/staging
  // events out of the prod project and avoids empty/null-token requests.
  if (!MIXPANEL_TOKEN) {
    return;
  }

  try {
    const { Mixpanel } = require('mixpanel-react-native');
    const client = new Mixpanel(MIXPANEL_TOKEN, true);
    await client.init(false, {}, MIXPANEL_SERVER_URL);
    client.setFlushOnBackground?.(true);
    mixpanelClient = client;
    if (analyticsAllowed) {
      optInNative();
      await rawRegisterSuperProperties(baseSuperProperties());
    } else {
      optOutNative();
    }
  } catch (error) {
    console.error('Failed to initialize Mixpanel:', error);
  }
};

// Cache the in-flight init promise so concurrent callers (the two useEffect hooks
// in app/_layout.tsx) don't race and overwrite mixpanelClient with a second instance.
export const initAnalytics = (): Promise<void> => {
  if (!initPromise) {
    initPromise = doInit();
  }
  return initPromise;
};

export const applyAnalyticsConsent = async (consent?: ConsentSettings | null): Promise<void> => {
  analyticsAllowed = consent?.analytics ?? (await canUseAnalytics());

  if (Platform.OS === 'web') {
    if (analyticsAllowed) {
      getWebClient()?.opt_in_tracking?.();
      await rawRegisterSuperProperties(baseSuperProperties());
      // MR2 (#100): enable GA4 on opt-in (and load it on first opt-in).
      try {
        await initGa4();
        ga4Provider.setConsent(true);
      } catch (error) {
        console.error('GA4 init/consent failed:', error);
      }
    } else {
      optOutWeb();
      try {
        ga4Provider.setConsent(false);
      } catch (error) {
        console.error('GA4 consent update failed:', error);
      }
    }
    return;
  }

  await initAnalytics();
  if (analyticsAllowed) {
    optInNative();
    await rawRegisterSuperProperties(baseSuperProperties());
  } else {
    optOutNative();
  }
};

const ensureAnalyticsAllowed = async (): Promise<boolean> => {
  if (!initPromise) {
    await initAnalytics();
  } else {
    await initPromise;
  }
  analyticsAllowed = await canUseAnalytics();
  if (!analyticsAllowed) {
    if (Platform.OS === 'web') optOutWeb();
    return false;
  }
  return true;
};

export const identify = async (distinctId: string | number) => {
  if (typeof distinctId === 'string' && /[@\s]/.test(distinctId)) return;

  const id = String(distinctId);
  // Idempotency: skip re-identifying the same user within the same session.
  if (id === lastIdentifiedId) return;

  if (!(await ensureAnalyticsAllowed())) return;

  lastIdentifiedId = id;

  // #389 blocker 6: capture the REAL provider identify DISPATCH at the adapter
  // boundary — WITHOUT copying any user identifier. The observer is test-only
  // and must never store PII (distinct_id/user_id/email/token); it records only
  // that the identify path was taken. The real provider below is unchanged.
  emitE2EObserver('identify', { called: true }, 'provider');

  // #299: GA4 first, outside the Mixpanel path, on every platform.
  emitGa4('identify', () => ga4Provider.identify(id));

  if (Platform.OS === 'web') {
    await emitMixpanel('identify', () => getWebClient()?.identify?.(id));
    return;
  }

  await emitMixpanel('identify', () => mixpanelClient?.identify(id));
};

/**
 * Clear the current Mixpanel identity and start a new anonymous session.
 * Must be called on logout to prevent events from the next user being attributed
 * to the previous one. Does NOT clear consent preferences.
 *
 * Web: mixpanel.reset() generates a new anonymous distinct_id in localStorage.
 * Native: mixpanel.reset() does the same via the React Native SDK.
 * GA4: kept anonymous-first — no identity to reset.
 *
 * SDK evidence: mixpanel-react-native v3.2.1 exports reset(); the web snippet
 * stub loaded in app/_layout.tsx explicitly includes "reset" in its method list.
 */
export const resetIdentity = (): void => {
  lastIdentifiedId = null;
  if (Platform.OS === 'web') {
    try {
      getWebClient()?.reset?.();
    } catch (error) {
      console.error('Mixpanel reset failed:', error);
    }
    return;
  }
  try {
    mixpanelClient?.reset?.();
  } catch (error) {
    console.error('Mixpanel reset failed:', error);
  }
};

const USER_PROPERTY_ALLOWLIST = new Set(['user_role']);

export const setUserProperties = async (properties: Record<string, any>) => {
  if (!(await ensureAnalyticsAllowed())) return;
  const clean = Object.fromEntries(
    Object.entries(properties || {}).filter(
      ([key, value]) => USER_PROPERTY_ALLOWLIST.has(key) && safeAnalyticsValue(value)
    )
  );
  if (Object.keys(clean).length === 0) return;

  // #299: GA4 first, outside the Mixpanel path, on every platform.
  emitGa4('setUserProperties', () => ga4Provider.setUserProperties(clean));

  if (Platform.OS === 'web') {
    await emitMixpanel('setUserProperties', () => getWebClient()?.people?.set?.(clean));
    return;
  }

  await emitMixpanel('setUserProperties', () => mixpanelClient?.getPeople()?.set?.(clean));
};

export const registerSuperProperties = async (properties: Record<string, any>) => {
  if (!(await ensureAnalyticsAllowed())) return;
  const clean = Object.fromEntries(
    Object.entries(properties || {}).filter(
      ([key, value]) =>
        ['platform', 'app_version', 'environment', 'user_role', 'source', 'campaign', 'medium'].includes(
          key,
        ) && safeAnalyticsValue(value)
    )
  );
  if (Object.keys(clean).length === 0) return;
  await rawRegisterSuperProperties(clean);
  // MR2 (#100): mirror the same (already allowlisted) super-properties to GA4 so
  // deep-link/utm attribution (#90) segments identically across both providers.
  try {
    ga4Provider.registerSuperProperties(clean);
  } catch (error) {
    console.error('GA4 registerSuperProperties failed:', error);
  }
};

// Segment every subsequent event by the user's role (owner | driver). Call
// once the authenticated user's role is known (e.g. after identify) so the
// owner→purchase funnel (#100) can be filtered without per-event plumbing.
export const setUserRole = async (role: 'owner' | 'driver' | 'unknown') => {
  await registerSuperProperties({ user_role: role });
};

/**
 * Track a canonical analytics event.
 *
 * Guarantees (#100):
 *  - Consent absent => zero Mixpanel/GA requests; anonymous funnel count only.
 *  - PII is stripped and the per-event allowlist is enforced.
 *  - `event_id` is minted (or preserved if the caller supplied one) and used
 *    for idempotent dedupe within the session.
 *  - `purchase_completed` is backend-webhook-only; checkout return never emits it.
 *  - `environment` is attached to every event and anonymous count.
 *  - Analytics failures never break the product flow.
 */
export const track = async (
  eventName: string,
  properties: Record<string, any> = {},
  options: { eventId?: string } = {},
): Promise<void> => {
  // Unknown events and purchase_completed are deliberately rejected at this
  // boundary. The webhook is the only source of truth for purchases.
  if (!allowedPropsFor(eventName) || eventName === 'purchase_completed') return;

  const requestedEventId = options.eventId || properties.event_id;
  const eventId =
    typeof requestedEventId === 'string' && safeInfraValue('event_id', requestedEventId)
      ? requestedEventId
      : mintEventId();
  if (!dedupeEventId(eventId)) return;

  if (!(await ensureAnalyticsAllowed())) {
    sendAnonymousFunnelCount(eventName, properties, eventId);
    return;
  }

  const clean = sanitizeProperties(eventName, properties);
  clean.event_id = eventId;
  clean.environment = ENVIRONMENT;

  // #389 blocker 6: capture the REAL provider dispatch at the adapter boundary.
  // Channel 'provider'. Enabled only by the explicit E2E flag, never in prod.
  emitE2EObserver(eventName, clean, 'provider');

  // #299: GA4 first, outside the Mixpanel path, on every platform. Previously
  // the native branch returned early when there was no Mixpanel client and
  // nested the GA4 call inside Mixpanel's try — so no token, or one throw from
  // Mixpanel, meant GA4 emitted nothing.
  emitGa4('track', () => ga4Provider.track(eventName, clean));

  if (Platform.OS === 'web') {
    await emitMixpanel('track', () => getWebClient()?.track?.(eventName, clean));
    return;
  }

  await emitMixpanel('track', () => mixpanelClient?.track(eventName, clean));
};

// Force-flush queued events to the network. Call after critical events
// (session/driving start/end) and on AppState=background.
export const flush = (): void => {
  if (!analyticsAllowed || Platform.OS === 'web') return;
  try {
    mixpanelClient?.flush?.();
  } catch (error) {
    console.error('Mixpanel flush failed:', error);
  }
  try {
    ga4Provider.flush();
  } catch (error) {
    console.error('GA4 flush failed:', error);
  }
};

export const resetAnalyticsForTests = (): void => {
  mixpanelClient = null;
  initPromise = null;
  analyticsAllowed = false;
  seenEventIds.clear();
  lastIdentifiedId = null;
  // Drop any test-only E2E observer sink so one test's observer cannot leak
  // into another. Note: this does NOT enable the observer — the harness flag
  // gates that independently.
  if (typeof window !== 'undefined') {
    delete (window as any)[E2E_OBSERVER_SINK];
  }
  try {
    resetGa4ForTests();
  } catch {
    // ignore — best-effort test reset
  }
};

/**
 * Read entry/attribution parameters (utm_*) from the current web URL and map
 * them to allowlisted analytics super-properties (#90 deep-link attribution).
 * On non-web platforms this returns an empty object. No cross-device install
 * identifier (GA4 FA Client ID) is read — privacy-first.
 */
export const captureAttributionParams = (): Record<string, string> => {
  if (typeof window === 'undefined' || !window.location?.search) return {};
  const params = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  const source = params.get('utm_source');
  const campaign = params.get('utm_campaign');
  const medium = params.get('utm_medium');
  if (source) out.source = source.slice(0, 64);
  if (campaign) out.campaign = campaign.slice(0, 64);
  if (medium) out.medium = medium.slice(0, 64);
  return out;
};
